import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { User, MapPin, Package, LogOut, KeyRound, ChevronRight, Heart, ShoppingCart, X } from "lucide-react";

const STATUS_LABEL = {
  processing: { label: "In lavorazione", cls: "bg-amber-100 text-amber-700" },
  shipped: { label: "Spedito", cls: "bg-xd-cyan/10 text-xd-cyanHover" },
  delivered: { label: "Consegnato", cls: "bg-xd-success/10 text-xd-success" },
  cancelled: { label: "Annullato", cls: "bg-red-50 text-red-500" },
};

const TABS = [
  { id: "orders", label: "I miei ordini", icon: Package },
  { id: "wishlist", label: "Preferiti", icon: Heart },
  { id: "profile", label: "Dati personali", icon: User },
  { id: "address", label: "Indirizzo", icon: MapPin },
  { id: "password", label: "Sicurezza", icon: KeyRound },
];

export default function CustomerAccount() {
  const { customer, loading, logout, updateProfile, changePassword, fetchMyOrders, fetchWishlist, removeFromWishlist } = useCustomerAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [profile, setProfile] = useState({ full_name: "", phone: "" });
  const [address, setAddress] = useState({ address_line: "", city: "", postal_code: "", country: "IT" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!customer) return;
    setProfile({ full_name: customer.name || "", phone: customer.phone || "" });
    const a = customer.address || {};
    setAddress({ address_line: a.address_line || "", city: a.city || "", postal_code: a.postal_code || "", country: a.country || "IT" });
    fetchMyOrders().then(setOrders).catch(() => {});
  }, [customer?.id]); // eslint-disable-line

  useEffect(() => {
    if (tab !== "wishlist" || !customer) return;
    setWishlistLoading(true);
    fetchWishlist().then(setWishlist).catch(() => {}).finally(() => setWishlistLoading(false));
  }, [tab, customer?.id]); // eslint-disable-line

  const dropWishlist = async (pid) => {
    try {
      await removeFromWishlist(pid);
      setWishlist((prev) => prev.filter((p) => p.id !== pid));
      toast.success("Rimosso dai preferiti");
    } catch { toast.error("Errore"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-xd-muted">Caricamento…</div>;
  if (!customer) return <Navigate to="/login?next=/account" replace />;

  const doLogout = () => { logout(); toast.success("Logout effettuato"); nav("/"); };

  const saveProfile = async () => {
    setSaving(true);
    try { await updateProfile(profile); toast.success("Dati aggiornati"); } catch { toast.error("Errore"); } finally { setSaving(false); }
  };
  const saveAddress = async () => {
    setSaving(true);
    try { await updateProfile(address); toast.success("Indirizzo salvato"); } catch { toast.error("Errore"); } finally { setSaving(false); }
  };
  const savePassword = async () => {
    if (pwd.new_password.length < 8) return toast.error("Nuova password minimo 8 caratteri");
    if (pwd.new_password !== pwd.confirm) return toast.error("Le password non coincidono");
    setSaving(true);
    try {
      await changePassword({ current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Password aggiornata");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-xd-light">
      <Header />
      <section className="bg-xd-dark text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-cyan">Il tuo account</div>
          <h1 className="font-display text-3xl font-semibold mt-2">Ciao, {customer.name?.split(" ")[0] || "cliente"} 👋</h1>
          <p className="text-slate-300 text-sm mt-1">{customer.email}</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-[240px_1fr] gap-6">
        <aside className="bg-white border border-xd-border rounded-xl p-3 h-fit">
          {TABS.map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`account-tab-${t.id}`} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${tab === t.id ? "bg-xd-cyan/10 text-xd-cyanHover" : "text-xd-text hover:bg-slate-50"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
          <button type="button" onClick={doLogout} data-testid="account-logout" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-500 hover:bg-red-50 mt-2 border-t border-xd-border pt-3">
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </aside>

        <div className="bg-white border border-xd-border rounded-xl p-6">
          {tab === "orders" && (
            <div>
              <div className="font-display text-xl font-semibold mb-4">I miei ordini</div>
              {orders.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-xd-border rounded-xl">
                  <Package className="w-8 h-8 mx-auto text-xd-muted" />
                  <div className="mt-3 text-sm text-xd-muted">Nessun ordine ancora</div>
                  <Link to="/catalogo" className="mt-4 inline-block text-xd-cyanHover text-sm font-medium hover:underline">Sfoglia il catalogo →</Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.map((o) => {
                    const st = STATUS_LABEL[o.status] || { label: o.status, cls: "bg-slate-100 text-slate-600" };
                    return (
                      <div key={o.id} data-testid={`account-order-${o.id}`} className="border border-xd-border rounded-lg p-4 hover:border-xd-cyan transition-colors">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div>
                            <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-muted">Ordine</div>
                            <div className="font-display font-semibold">{o.order_number}</div>
                            <div className="text-xs text-xd-muted mt-1">{new Date(o.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</div>
                          </div>
                          <div className="text-right">
                            <span className={`text-xs px-2 py-0.5 rounded ${st.cls}`}>{st.label}</span>
                            <div className="font-display font-semibold mt-1">€ {o.total.toFixed(2)}</div>
                          </div>
                        </div>
                        <div className="mt-3 border-t border-xd-border pt-3 space-y-1.5">
                          {o.items.map((i, idx) => (
                            <div key={idx} className="flex items-center gap-3 text-sm">
                              <div className="w-8 h-8 rounded bg-slate-100 overflow-hidden flex-shrink-0">
                                {i.image && <img src={i.image} alt="" className="w-full h-full object-cover" />}
                              </div>
                              <span className="flex-1 truncate">{i.name}</span>
                              <span className="text-xs text-xd-muted">×{i.quantity}</span>
                            </div>
                          ))}
                        </div>
                        {o.tracking_number && (
                          <div className="mt-3 flex items-center gap-2 text-xs text-xd-cyanHover">
                            <ChevronRight className="w-3 h-3" /> Tracking: <span className="font-mono-tech font-semibold">{o.tracking_number}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "wishlist" && (
            <div data-testid="wishlist-panel">
              <div className="font-display text-xl font-semibold mb-4">I miei preferiti</div>
              {wishlistLoading ? (
                <div className="py-12 text-center text-xd-muted text-sm">Caricamento…</div>
              ) : wishlist.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-xd-border rounded-xl">
                  <Heart className="w-8 h-8 mx-auto text-xd-muted" />
                  <div className="mt-3 text-sm text-xd-muted">Nessun preferito ancora</div>
                  <p className="text-xs text-xd-muted mt-1 max-w-xs mx-auto">Tocca il cuore su un prodotto del catalogo per salvarlo qui.</p>
                  <Link to="/catalogo" className="mt-4 inline-block text-xd-cyanHover text-sm font-medium hover:underline">Sfoglia il catalogo →</Link>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {wishlist.map((p) => (
                    <div key={p.id} data-testid={`wishlist-row-${p.id}`} className="flex gap-3 border border-xd-border rounded-lg p-3 hover:border-xd-cyan transition-colors">
                      <Link to={`/prodotto/${p.id}`} className="w-20 h-20 rounded bg-slate-100 overflow-hidden flex-shrink-0">
                        {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">{p.brand} · {p.model}</div>
                        <Link to={`/prodotto/${p.id}`} className="block font-display text-sm font-medium leading-tight text-xd-text hover:text-xd-cyanHover line-clamp-2">
                          {p.name}
                        </Link>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <div className="font-display font-semibold text-sm">€ {Number(p.price).toFixed(2)}</div>
                          <div className="flex gap-1.5">
                            <Link to={`/prodotto/${p.id}`} data-testid={`wishlist-buy-${p.id}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs bg-xd-orange text-white hover:opacity-90">
                              <ShoppingCart className="w-3 h-3" /> Vedi
                            </Link>
                            <button type="button" onClick={() => dropWishlist(p.id)} data-testid={`wishlist-remove-${p.id}`} className="inline-flex items-center justify-center h-8 w-8 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50" aria-label="Rimuovi">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "profile" && (
            <div>
              <div className="font-display text-xl font-semibold mb-4">Dati personali</div>
              <div className="grid sm:grid-cols-2 gap-3 max-w-xl">
                <Input placeholder="Nome e cognome" value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} data-testid="profile-fullname" />
                <Input placeholder="Telefono" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="profile-phone" />
                <Input value={customer.email} disabled className="sm:col-span-2 opacity-60" />
              </div>
              <button type="button" onClick={saveProfile} disabled={saving} data-testid="profile-save" className="btn-cta rounded-lg mt-4 px-6 py-2.5 text-sm">Salva</button>
            </div>
          )}

          {tab === "address" && (
            <div>
              <div className="font-display text-xl font-semibold mb-4">Indirizzo predefinito</div>
              <div className="grid sm:grid-cols-2 gap-3 max-w-xl">
                <Input placeholder="Indirizzo (via, numero)" value={address.address_line} onChange={(e) => setAddress({ ...address, address_line: e.target.value })} className="sm:col-span-2" data-testid="address-line" />
                <Input placeholder="Città" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} data-testid="address-city" />
                <Input placeholder="CAP" value={address.postal_code} onChange={(e) => setAddress({ ...address, postal_code: e.target.value })} data-testid="address-postal" />
                <Input placeholder="Paese" value={address.country} onChange={(e) => setAddress({ ...address, country: e.target.value })} data-testid="address-country" />
              </div>
              <button type="button" onClick={saveAddress} disabled={saving} data-testid="address-save" className="btn-cta rounded-lg mt-4 px-6 py-2.5 text-sm">Salva indirizzo</button>
            </div>
          )}

          {tab === "password" && (
            <div>
              <div className="font-display text-xl font-semibold mb-4">Cambio password</div>
              <div className="grid gap-3 max-w-md">
                <Input type="password" placeholder="Password attuale" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="pwd-current" />
                <Input type="password" placeholder="Nuova password (min 8 caratteri)" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="pwd-new" />
                <Input type="password" placeholder="Conferma nuova password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="pwd-confirm" />
              </div>
              <button type="button" onClick={savePassword} disabled={saving} data-testid="pwd-save" className="btn-cta rounded-lg mt-4 px-6 py-2.5 text-sm">Aggiorna password</button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
